import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormParagraph, UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputControl } from '../../../../elements/forms/inputControl/InputControl';
import { NullableBoolean } from '../../../../elements/NullableBoolean';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { HideRequestedExaminationInTaskListAndHomeCheckbox } from '../../../requests/HideRequestedExaminationInTaskListAndHomeCheckbox';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { TiecgV1RequestSummary } from './TiecgV1RequestSummary';

export function TiecgV1RequestRead(props: ICustomControlRequestReadProps) {
    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isExaminationRequest
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <TiecgV1RequestSummary {...props} />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <HorizontalFormGroupRead>
                    <FormLabel>Voorgeschiedenis en klachten</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>
                            {props.request.customControlData.voorgeschiedenis_klachten}
                        </UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
                <HorizontalFormGroupRead>
                    <FormLabel>Bloeddruk</FormLabel>
                    <InputContainer>
                        <InputControl
                            inline
                            isFormControl
                            readOnly
                        >
                            {props.request.customControlData.requestCriteriaData &&
                            props.request.customControlData.requestCriteriaData.bloeddruk_systolisch
                                ? 'Systolisch ' +
                                  props.request.customControlData.requestCriteriaData.bloeddruk_systolisch +
                                  ' mmHg'
                                : ''}
                        </InputControl>
                        <InputControl
                            inline
                            isFormControl
                            readOnly
                        >
                            {props.request.customControlData.requestCriteriaData &&
                            props.request.customControlData.requestCriteriaData.bloeddruk_diastolisch
                                ? 'Diastolisch ' +
                                  props.request.customControlData.requestCriteriaData.bloeddruk_diastolisch +
                                  ' mmHg'
                                : ''}
                        </InputControl>
                    </InputContainer>
                </HorizontalFormGroupRead>
                <HorizontalFormGroupRead>
                    <FormLabel>Lichamelijk onderzoek</FormLabel>
                    <InputContainer>
                        <UserTextParagraph>{props.request.customControlData.lichamelijkOnderzoek}</UserTextParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
                <HorizontalFormGroupRead>
                    <FormLabel>Gebruikt bètablokker</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            <NullableBoolean
                                value={
                                    props.request.customControlData.gebruiktBetablokker !== null
                                        ? props.request.customControlData.gebruiktBetablokker
                                        : undefined
                                }
                            />
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>
                {props.request.customControlData.opmerkingen && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Opmerkingen</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{props.request.customControlData.opmerkingen}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <ProfileImages request={props.request} />
            </CustomControlCollapseBody>

            <ProcessItemFooter
                isItemOpen={props.itemState.isOpen}
                labelLess={translateKey('request.hideRequest')}
                labelMore={translateKey('request.showRequest')}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
            />
            <RequestReadButtons {...props} />
            <HideRequestedExaminationInTaskListAndHomeCheckbox
                isLastItem={props.isLastItem}
                reload={props.reloadRequest}
                request={props.request}
            />
        </ProcessContainer>
    );
}
